import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { FormContext, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ModalSidebarContent } from '../../../../components/ModalSidebar';
import { EmployeeOption } from '../../../../components/Selects/EmployeeOptionLabel';
import { useUserInfo } from '../../../../context/UserInfo';
import { GoalsActivities } from '../GoalsActivities/GoalsActivities';
import { GoalActivity, GoalId, GoalRelatedGoal } from '../types';
import { GoalOptions } from '../useGoalSelectOptions';
import { GoalCommonFields } from './GoalCommonFields';
import { GoalSidebarMode, ObjectiveFormData } from './types';
import { useSubmitUpdate, useUpdateObjectiveForm } from './useObjectiveForm';
import { GoalHierarchy } from './GoalHierarchy/GoalHierarchy';
import { GoalHierarchyItem } from './GoalHierarchy/types';
import { GoalAlignedToConnected } from './GoalAlignedTo/GoalAlignedToConnected';
import { useGoalsContext } from '../GoalsContext/GoalsContext';

type Props = {
  owner?: EmployeeOption;
  goalId: GoalId;
  mode: GoalSidebarMode;
  options: GoalOptions;
  formData: ObjectiveFormData;
  goalWithHierarchy: GoalHierarchyItem;
  activities: GoalActivity[];
  onClose: () => void;
  alignedTo: GoalRelatedGoal[];
};

export const GoalUpdateForm: React.FC<Props> = (props) => {
  const form = useUpdateObjectiveForm(props.formData);

  const { reset } = form;

  useEffect(() => {
    reset(props.formData);
  }, [reset, props.formData]);

  return (
    <FormContext {...form}>
      <Form>
        <GoalUpdateFormContent {...props} />
      </Form>
    </FormContext>
  );
};

const GoalUpdateFormContent: React.FC<Props> = ({
  goalId,
  formData,
  goalWithHierarchy,
  mode,
  activities,
  onClose,
  alignedTo,
  options,
  ...restProps
}) => {
  const { t } = useTranslation();
  const form = useFormContext<ObjectiveFormData>();
  const { developmentFrameworkEnabled } = useGoalsContext();
  const { id: meId } = useUserInfo();
  const { formState } = form;
  const { dirtyFields, isSubmitting, isValid } = formState;

  const { submitUpdate, saveInfo } = useSubmitUpdate(meId, goalId);

  useEffect(() => {
    if (isSubmitting || !isValid || mode !== 'update') {
      return;
    }
    if (dirtyFields.size > 0) {
      submitUpdate(dirtyFields);
    }
  }, [dirtyFields, submitUpdate, isSubmitting, mode, isValid]);

  const goalHierarchy = (
    <div className="mb-3">
      <GoalHierarchy rootGoal={goalWithHierarchy} currentGoalId={goalId.id} />
    </div>
  );

  const goalAlignedTo =
    goalId.type === 'employee' && developmentFrameworkEnabled ? (
      <div className="mb-3">
        <GoalAlignedToConnected goalId={goalId} alignedTo={alignedTo} />
      </div>
    ) : null;

  return (
    <ModalSidebarContent
      body={
        <div>
          <input type="hidden" name="type" ref={form.register} />
          <GoalCommonFields
            {...restProps}
            mode={mode}
            saveInfo={saveInfo}
            goalHierarchy={goalHierarchy}
            goalAlignedTo={goalAlignedTo}
            options={options}
          />
          <ActivityHeader className="mb-3">
            {t('performanceReview.goals2.activityTitle')}
          </ActivityHeader>
          <GoalsActivities activities={activities} />
        </div>
      }
      footer={null}
    />
  );
};

const Form = styled('form')({
  display: 'contents',
});

const ActivityHeader = styled('div')({
  fontWeight: 600,
  fontSize: 15,
});
